import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/Brand";
import { useData } from "@/lib/api";
import { fmtTime, roleLabel } from "@/lib/crm";
import { useAuth } from "@/context/AuthContext";

const ALL = "__all__";

function groupFeed(items) {
  const groups = [];
  items.forEach((a) => {
    const day = new Date(a.created_at).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    const key = `${a.user_id}|${day}`;
    let g = groups.find((x) => x.key === key);
    if (!g) groups.push((g = { key, day, user: a.user_name, role: a.user_role, items: [] }));
    g.items.push(a);
  });
  return groups;
}

export function ActivityFeed({ items }) {
  if (!items.length) return <div className="rounded-2xl border border-dashed p-12 text-center text-stone-500" data-testid="activity-empty">No activity yet.</div>;
  return (
    <div className="space-y-6" data-testid="activity-feed">
      {groupFeed(items).map((g) => (
        <div key={g.key} className="rounded-2xl border bg-white overflow-hidden fade-up">
          <div className="flex flex-wrap items-baseline justify-between gap-2 bg-[#19082B] px-5 py-3 text-white">
            <div className="font-display text-lg font-bold">{g.user} <span className="text-[#D4AF37] font-medium">— {roleLabel(g.role)}</span></div>
            <div className="text-xs text-white/60">{g.day}</div>
          </div>
          <ul className="divide-y">
            {g.items.map((a) => (
              <li key={a.id} className="flex gap-4 px-5 py-3 text-sm" data-testid={`activity-item-${a.id}`}>
                <span className="w-20 shrink-0 font-mono text-xs text-[#C59B27] pt-0.5">{fmtTime(a.created_at)}</span>
                <span className="flex-1">
                  <span className="font-semibold text-[#19082B]">{a.action}</span>
                  {a.contact_name && <span className="text-stone-600"> · {a.contact_name}</span>}
                  {a.campaign_name && <span className="text-stone-600"> · {a.campaign_name}</span>}
                  {a.detail && <span className="text-stone-400"> · {a.detail}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function AdminActivity({ initialUser }) {
  const { user } = useAuth();
  const [who, setWho] = useState(initialUser || ALL);
  const [students] = useData("/admin/students", []);
  const [items] = useData(`/admin/activity${who !== ALL ? `?user_id=${who}` : ""}`, []);
  return (
    <div className="space-y-5">
      <Select value={who} onValueChange={setWho}>
        <SelectTrigger className="w-72" data-testid="activity-user-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All users</SelectItem>
          <SelectItem value={user.id}>{user.name} (Owner)</SelectItem>
          {students.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`activity-filter-option-${s.id}`}>{s.name} — {s.status}</SelectItem>)}
        </SelectContent>
      </Select>
      <ActivityFeed items={items} />
    </div>
  );
}

export default function Activity() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const isAdmin = user.role === "admin";
  const [items] = useData(isAdmin ? null : "/activity", []);
  return (
    <div>
      <PageHeader title={isAdmin ? "Activity" : "My Activity"} subtitle={isAdmin ? "Everything that happens in the workspace, by user and time." : "A record of your work in the workspace."} />
      {isAdmin ? <AdminActivity initialUser={params.get("user")} /> : <ActivityFeed items={items} />}
    </div>
  );
}
